/**
 * WorkspaceContext
 *
 * Tracks which workspace the user is currently operating against. The active
 * workspace ID is sent to the backend as ``X-Workspace-Id`` (see the axios
 * interceptor in App.js) and gates billing / quota behaviour.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';

const STORAGE_KEY = 'onx_active_workspace';
const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const WorkspaceContext = createContext({
  workspaces: [],
  activeWorkspaceId: null,
  activeWorkspace: null,
  loading: false,
  refresh: () => {},
  switchWorkspace: () => {},
  isSwitching: false,
  switchingTo: null,
  isSuperAdmin: false,
  isInSuperAdminMode: false,
  workspaceRole: null,        // 'owner' | 'admin' | 'member' | 'viewer' | 'super_admin' | null
  isWorkspaceOwner: false,
  isWorkspaceAdmin: false,    // owner OR admin OR super_admin
  canWriteInWorkspace: false, // owner OR admin OR member OR super_admin
});

export function WorkspaceProvider({ children }) {
  const { user, checkAuth } = useContext(AuthContext);
  const userId = user?.id || null;
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(false);
  // checkAuth App.js'de useCallback ile sarılı değil — her render'da yeni
  // referans alır. useEffect dependency'sine koyulursa sonsuz döngü olur.
  // Ref ile en güncel referansı sakla, useEffect bunu sadece okusun.
  const checkAuthRef = useRef(checkAuth);
  useEffect(() => { checkAuthRef.current = checkAuth; }, [checkAuth]);
  // Workspace permission tazeleme akışı yalnızca aynı user içinde bir kez
  // çalışsın; bu olmadan refresh→checkAuth→setUser→useEffect tekrar tetikler
  // ve loop'a girer.
  const reauthDoneForUserRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!userId) return null;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/workspaces/me`);
      const list = response.data?.workspaces || [];
      setWorkspaces(list);

      // Resolve the active id with a clear precedence:
      //   localStorage > server default > first available
      const stored = localStorage.getItem(STORAGE_KEY);
      const known = list.find(w => w.id === stored);
      let resolvedId = null;
      if (known) {
        resolvedId = known.id;
      } else if (response.data?.defaultWorkspaceId) {
        resolvedId = response.data.defaultWorkspaceId;
        localStorage.setItem(STORAGE_KEY, resolvedId);
      } else if (list.length) {
        resolvedId = list[0].id;
        localStorage.setItem(STORAGE_KEY, resolvedId);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
      setActiveWorkspaceId(resolvedId);
      return resolvedId;
    } catch (err) {
      console.error('[Workspace] Failed to load workspaces:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Login akışında /auth/me, localStorage'da onx_active_workspace henüz set
  // edilmeden çağrıldığı için backend X-Workspace-Id alamaz; permissions
  // global rol fallback'iyle döner. WorkspaceProvider workspace listesini
  // çekip aktif id'yi resolve ettikten sonra /auth/me'i bir kez daha çağırarak
  // user.permissions'ı doğru workspace context'inde tazeler.
  useEffect(() => {
    if (!userId) {
      setWorkspaces([]);
      setActiveWorkspaceId(null);
      localStorage.removeItem(STORAGE_KEY);
      reauthDoneForUserRef.current = null;
      return;
    }
    let cancelled = false;
    (async () => {
      const id = await refresh();
      if (cancelled) return;
      if (!id) return;
      // Aynı kullanıcı için tek bir kez tazele — aksi halde checkAuth
      // setUser tetikler, useEffect tekrar koşar ve loop oluşur.
      if (reauthDoneForUserRef.current === userId) return;
      reauthDoneForUserRef.current = userId;
      const fn = checkAuthRef.current;
      if (fn) {
        try { await fn(); } catch (_) { /* sessizce */ }
      }
    })();
    return () => { cancelled = true; };
  }, [userId, refresh]);

  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);

  const switchWorkspace = useCallback((id) => {
    if (!id) return;
    if (id === activeWorkspaceId) return;
    if (isSwitching) return;

    // Açık ve kirli (dirty) form varsa kullanıcıya doğrula. Form bileşenleri
    // beforeunload listener'ı kayıt etmiş olmalı; biz burada yalnızca
    // hard-reload öncesi son uyarıyı veriyoruz. confirm() için fallback.
    const dirtyEl = document.querySelector('[data-dirty="true"], form[data-dirty="true"]');
    if (dirtyEl) {
      const ok = window.confirm(
        'Kaydedilmemiş değişiklikleriniz var. Workspace değiştirirseniz kaybolacaklar. Devam edilsin mi?'
      );
      if (!ok) return;
    }

    localStorage.setItem(STORAGE_KEY, id);
    setSwitchingTo(id);
    setIsSwitching(true);

    // Workspace değişiminde her zaman dashboard'a düş — kullanıcı yeni
    // workspace'in genel görünümüyle karşılaşsın, eski sayfada (örn. başka
    // workspace'in proje detayı) garip empty-state ile değil.
    setTimeout(() => { window.location.assign('/dashboard'); }, 50);
  }, [activeWorkspaceId, isSwitching]);

  const activeWorkspace = useMemo(
    () => workspaces.find(w => w.id === activeWorkspaceId) || null,
    [workspaces, activeWorkspaceId],
  );

  const isSuperAdmin = user?.role === 'super_admin';
  // Super admin "viewing as" mode: active workspace exists but user has only synthetic membership
  // (i.e. the backend marked it isSuperAdminView and they're not a real member).
  const isInSuperAdminMode = useMemo(() => {
    if (!isSuperAdmin || !activeWorkspace) return false;
    return activeWorkspace.isSuperAdminView === true;
  }, [isSuperAdmin, activeWorkspace]);

  const workspaceRole = useMemo(() => {
    if (isSuperAdmin) return 'super_admin';
    return activeWorkspace?.membership?.role || null;
  }, [isSuperAdmin, activeWorkspace]);

  const isWorkspaceOwner = workspaceRole === 'owner' || isSuperAdmin;
  const isWorkspaceAdmin = ['owner', 'admin', 'super_admin'].includes(workspaceRole);
  const canWriteInWorkspace = ['owner', 'admin', 'member', 'super_admin'].includes(workspaceRole);

  const value = useMemo(() => ({
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    loading,
    refresh,
    switchWorkspace,
    isSwitching,
    switchingTo,
    isSuperAdmin,
    isInSuperAdminMode,
    workspaceRole,
    isWorkspaceOwner,
    isWorkspaceAdmin,
    canWriteInWorkspace,
  }), [workspaces, activeWorkspaceId, activeWorkspace, loading, refresh, switchWorkspace, isSwitching, switchingTo, isSuperAdmin, isInSuperAdminMode, workspaceRole, isWorkspaceOwner, isWorkspaceAdmin, canWriteInWorkspace]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  return useContext(WorkspaceContext);
}

export function getActiveWorkspaceIdFromStorage() {
  return localStorage.getItem(STORAGE_KEY);
}
